import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Paper } from 'shared/components/Paper';
import { Skeleton } from 'shared/components/Skeleton';

const JobOrderRows = () => (
  <div className="space-y-4" aria-hidden="true">
    {[0, 1, 2].map((row) => (
      <div key={row} className="space-y-4 rounded-lg bg-gray-200 p-5 sm:p-6">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-5 w-5/6" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-5 h-12 w-full sm:hidden" />
      </div>
    ))}
  </div>
);

export const PageSkeleton = ({
  variant,
}: {
  variant?: 'home' | 'history' | 'details' | 'comparer';
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  let page = variant;
  if (!page) {
    page = 'home';
    if (pathname.endsWith('/job-orders-history')) page = 'history';
    if (pathname.includes('/job-order/') || pathname.includes('/archived-job-order/'))
      page = 'details';
    if (pathname.includes('/blinds-comparison')) page = 'comparer';
  }

  return (
    <div role="status" aria-label={t('common:loading')}>
      <span className="sr-only">{t('common:loading')}</span>
      <div aria-hidden="true">
        {page === 'history' ? (
          <h1 className="heading-2 mb-6 md:mb-8">{t('home:jobOrdersHistory.title')}</h1>
        ) : (
          <div className="mb-6 md:mb-8">
            {(page === 'details' || page === 'comparer') && <Skeleton className="mb-4 h-9 w-24" />}
            {page === 'details' && <Skeleton className="mb-2 h-4 w-32" />}
            <div className="flex h-[30px] items-center md:h-10">
              <Skeleton className="h-7 w-56 md:h-9" />
            </div>
            {page === 'details' && <Skeleton className="mt-2 h-5 w-24" />}
          </div>
        )}
        {page === 'home' && (
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_351px] lg:gap-7">
            <div className="min-w-0 space-y-6">
              <Paper
                className="notification-panel"
                title={t('home:notifications.title')}
                headerActions={<Skeleton className="h-9 w-32 bg-white/20" />}
              >
                <div className="min-h-44 space-y-4 rounded-lg bg-white/10 p-5 md:min-h-36">
                  <Skeleton className="h-4 w-24 bg-white/20" />
                  <Skeleton className="h-5 w-5/6 bg-white/20" />
                  <Skeleton className="h-5 w-2/3 bg-white/20" />
                  <Skeleton className="h-4 w-28 bg-white/20" />
                </div>
              </Paper>
              <Paper size="lg" title={t('home:activeJobOrders.title')}>
                <JobOrderRows />
              </Paper>
            </div>
            <Paper title={t('home:sales.title')}>
              <div className="space-y-5 rounded-lg bg-gray-200 p-4">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-5 w-full" />
              </div>
            </Paper>
          </div>
        )}
        {page === 'history' && (
          <div className="space-y-6">
            {(['positivelyFinished', 'negativelyFinished', 'otherFinished'] as const).map(
              (category) => (
                <Paper key={category} size="lg" title={t(`home:jobOrdersHistory.${category}`)}>
                  <JobOrderRows />
                </Paper>
              ),
            )}
          </div>
        )}
        {page === 'details' && (
          <div className="space-y-6">
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
              {[0, 1].map((item) => (
                <Paper key={item}>
                  <Skeleton className="mb-6 h-7 w-48" />
                  <Skeleton className="h-32 w-full" />
                </Paper>
              ))}
            </div>
            <Paper>
              <Skeleton className="mb-6 h-7 w-48" />
              <JobOrderRows />
            </Paper>
          </div>
        )}
        {page === 'comparer' && (
          <div className="shadow-paper overflow-hidden rounded-2xl bg-white">
            <div className="grid min-w-[1050px] grid-cols-6 divide-x divide-black/10">
              {[0, 1, 2, 3, 4, 5].map((column) => (
                <div key={column} className="space-y-8 p-6">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-24 w-full" />
                  {[0, 1, 2, 3].map((row) => (
                    <Skeleton key={row} className="h-6 w-full" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
